import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import styled, { withTheme } from 'styled-components';
import { Button } from 'components/Button';
import { ContainerWrapper } from 'components/Card/cardStyle';
import Container from 'components/Container';
import ContainerHeader from 'components/ContainerHeader';
import { SearchInput } from 'components/Form/SearchInput';
import { HelpModals } from 'components/HelpModals';
import Icon from 'components/Icon';
import LineHeightText from 'components/LineHeightText';
import { ConfirmModal } from 'components/Modal';
import Pagination from 'components/Pagination';
import { Table } from 'components/Table';
import Text from 'components/Text';
import { TransactionModal } from 'components/Transactions';
import {
  withEventYearFilterContext,
  YearDropdown,
} from 'components/YearDropdown';
import { useUpdateQueryString } from 'hooks/useSearchInput';
import { IssueCreditModal } from 'pages/app/Credits/issueCreditModal';
import ExportOpenCreditsReport from 'pages/app/Credits/OpenCredits/ExportOpenCredits/ExportOpenCreditsReport';
import { PrintOpenCreditsReport } from 'pages/app/Credits/OpenCredits/ExportOpenCredits/PrintOpenCreditsReport';
import DeleteCredit from 'pages/app/Credits/OpenCredits/gql/delete-credit';
import { TitleContainer } from 'pages/track/Contact/style';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { logDevError } from 'shared/alerts';
import { AppContext } from 'shared/AppContext';
import { useGetUserOpenCreditsByAccount } from './gql/get-user-open-credits-by-account';

const CreditButton = styled.div`
  padding: 0;
  margin-left: auto;
  margin-right: 40px;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: 0;
  }
`;

const FilterContainer = styled.div`
  min-width: 150px;
`;

const TrackOpenCreditsDetail = withEventYearFilterContext((props) => {
  const { deleteCredit, history, location, match, theme } = props;
  const [credits, setCredits] = useState();
  const [count, setCount] = useState();
  const [search, setSearch] = useState('');

  const { id } = useParams();
  const userId = parseInt(id);

  const thisYear = Number(moment().format('YYYY'));
  const [year, setYear] = useState({ value: thisYear });
  const {
    state: { accountId, capabilities },
  } = useContext(AppContext);
  const [showIssueCredit, setShowIssueCredit] = useState(false);
  const [shouldDisplayDeleteConfirmation, setShouldDisplayDeleteConfirmation] =
    useState(false);
  const [credit, setCredit] = useState();
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const [currentTransaction, setCurrentTransaction] = useState(null);

  const { res, loading } = useGetUserOpenCreditsByAccount();

  useEffect(() => {
    if (res && res.getUserOpenCreditsByAccount) {
      setCredits(res?.getUserOpenCreditsByAccount?.results);
      setCount(res?.getUserOpenCreditsByAccount?.count);
    }
  }, [res]);

  const updateQueryString = useUpdateQueryString();

  const handleChange = (e) => setSearch(e.target.value);

  const handleBlur = (e) => {
    updateQueryString(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === 13) {
      updateQueryString(e.target.value);
    }
  };

  const columns = [
    {
      label: 'Ticket Holder',
      key: 'ticketHolder',
    },
    {
      label: 'Credit',
      key: 'credit',
    },
    {
      label: 'Credit #',
      key: 'creditNum',
    },
    {
      label: 'Issued',
      key: 'issued',
    },
    {
      label: 'Issued By',
      key: 'issuedBy',
    },
    {
      label: 'Original Total',
      key: 'originalTotal',
    },
    {
      label: 'Track Credit',
      key: 'trackCredit',
    },
    {
      label: 'Fee Credit',
      key: 'feeCredit',
    },
    {
      label: 'Purchase',
      key: 'purchase',
    },
    {
      label: '',
      key: 'actions',
    },
  ];

  const urlParams = new URLSearchParams(window.location.search);
  const currentPage = parseInt(urlParams.get('page'));

  function renderRows(credit) {
    const {
      id,
      user,
      amount,
      date,
      issued_by,
      original_amount,
      track_credit,
      fee_credit,
      transaction,
    } = credit;

    const userName = user.formatted_name;
    const issuer = issued_by.formatted_name;

    return {
      ticketHolder: userName,
      credit: amount.toString(),
      creditNum: id,
      issued: moment(date, 'MM-DD-YYYY')
        .format('ddd MMM d - YYYY')
        .toUpperCase(),
      issuedBy: issuer,
      originalTotal: original_amount,
      trackCredit: track_credit,
      feeCredit: fee_credit,
      purchase: (
        <div
          style={{
            color: theme.colors.primary,
            cursor: 'pointer',
          }}
          onClick={() => setCurrentTransaction(transaction)}
          role="presentation"
        >
          <LineHeightText>
            {' '}
            {transaction && transaction.id ? 'View' : ''}
          </LineHeightText>
        </div>
      ),
      actions: (
        <>
          <Icon
            onClick={() => {
              if (!capabilities || capabilities?.issue_credits) {
                setCredit({ id: id, user_id: user.id });
                setShouldDisplayDeleteConfirmation(true);
              } else setShowPermissionModal(true);
            }}
            size={18}
            color={props.theme.colors.primary}
            icon="trash"
          />
        </>
      ),
    };
  }

  async function handleDeleteConfirm() {
    try {
      const response = await deleteCredit(
        { ...credit, user_id: userId },
        {
          account_id: Number(accountId),
          user_id: userId,
          year: year.value,
        }
      );
      if (response && !response.errors) {
        toast.success('Credit deleted successfully');
        setShouldDisplayDeleteConfirmation(false);
      } else {
        toast.error('Error Deleting Credit');
      }
    } catch (error) {
      logDevError(error);
      toast.error('Error Deleting Credit');
    }
  }

  return loading ? (
    <div />
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'Row',
          justifyContent: 'end',
          marginTop: 20,
          marginRight: 40,
        }}
      >
        <ExportOpenCreditsReport
          headerText={''}
          year={year.value}
          isTrackAdmin={true}
        />

        <PrintOpenCreditsReport
          headerText={''}
          year={year.value}
          isTrackAdmin={true}
        />

        <HelpModals />
      </div>
      <Container>
        <ContainerHeader>
          <ContainerWrapper>
            <TitleContainer>
              <Text
                type="heading"
                color="#3C4144"
                inlineStyle={{ marginRight: 25, marginTop: 10 }}
              >
                Open Credits
              </Text>
              <FilterContainer>
                <YearDropdown
                  showLabel={false}
                  onSelect={(year) => setYear(year)}
                />
              </FilterContainer>
              <div style={{ marginLeft: 15 }}>
                <SearchInput
                  placeholder="Search Credits"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyPress}
                  value={search}
                />
              </div>
            </TitleContainer>
            <CreditButton>
              <Button
                onClick={() =>
                  !capabilities || capabilities?.issue_credits
                    ? setShowIssueCredit(true)
                    : setShowPermissionModal(true)
                }
                buttonStyle={{ height: 35 }}
              >
                {'Issue Credit'}
              </Button>
            </CreditButton>
          </ContainerWrapper>
        </ContainerHeader>
        {!credits ? (
          <div />
        ) : (
          <div style={{ padding: 20 }}>
            <Table items={credits} columns={columns} renderRows={renderRows} />
            <Pagination
              count={count}
              perPage={15}
              currentPage={currentPage || 1}
            />
          </div>
        )}
      </Container>
      <IssueCreditModal
        setIsVisible={setShowIssueCredit}
        isVisible={showIssueCredit}
        isTrackAdmin={true}
        vieEmployeeSearch={false}
        customerId={userId ?? ''}
        credits={credits}
        setCredits={setCredits}
      />

      <ConfirmModal
        confirmText="Delete"
        cancelText="Cancel"
        hideModal={() => setShouldDisplayDeleteConfirmation(false)}
        isVisible={shouldDisplayDeleteConfirmation}
        onConfirm={() => handleDeleteConfirm()}
        title="Do you want to delete this Credit?"
      />

      <PermissionModal
        isVisible={showPermissionModal}
        hideModal={() => setShowPermissionModal(false)}
        history={history}
      />

      {currentTransaction ? (
        <TransactionModal
          admin={location.pathname.includes('/admin/transactions/')}
          match={match}
          currentTransaction={currentTransaction}
          close={() => {
            setCurrentTransaction(null);
          }}
        />
      ) : null}
    </>
  );
});

export default withTheme(DeleteCredit(TrackOpenCreditsDetail));
